import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from "@angular/core";

import { HttpClient } from "@angular/common/http";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { ClassicEditor, EditorConfig } from "ckeditor5";
import { TranslateModule } from "@ngx-translate/core";

import { HelpSection } from "../../models/help-section.model";
import { buildCkeditorConfig } from "../../utils/ckeditor.config";
import { CkUploadAdapter } from "../../utils/ckeditor-uploader";
import {
  moveSectionByOffset,
  normalizeSectionTitleKey,
  uniqueSectionId,
} from "../../utils/help-document.util";
import {
  attachHelpEditorPasteSanitizer,
  sanitizeEditorOutput,
} from "../../utils/help-html-sanitize.util";

/**
 * Editor de ayuda con menú lateral de secciones y un único CKEditor activo.
 */
@Component({
  selector: "app-help-section-editor",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    CKEditorModule,
    TranslateModule,
  ],
  templateUrl: "./help-section-editor.component.html",
  styleUrl: "./help-section-editor.component.scss",
})
export class HelpSectionEditorComponent implements OnChanges {
  private http = inject(HttpClient);

  /** Idioma de la pestaña (es, en, …) para sincronizar con el padre al guardar */
  @Input() langId = "";

  @Input() sections: HelpSection[] = [];
  @Output() sectionsChange = new EventEmitter<HelpSection[]>();

  public editor: any = ClassicEditor;
  public editorConfig: EditorConfig = buildCkeditorConfig();
  public isLayoutReady = true;

  public activeSectionId: string | null = null;
  public sectionContentControl = new FormControl("");

  private syncingFromParent = false;
  private editorInstance: { getData: () => string } | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["sections"]) {
      const incoming: HelpSection[] = changes["sections"].currentValue ?? [];
      this.sections = incoming.map((s) => ({ ...s }));

      if (this.sections.length === 0) {
        this.activeSectionId = null;
        this.sectionContentControl.setValue("", { emitEvent: false });
        return;
      }

      const exists = this.sections.some((s) => s.id === this.activeSectionId);
      if (!exists) {
        this.activeSectionId = this.sortedSections[0].id;
      }
      this.loadActiveSectionIntoEditor();
    }
  }

  get sortedSections(): HelpSection[] {
    return [...this.sections].sort((a, b) => a.order - b.order);
  }

  get hasSections(): boolean {
    return this.sections.length > 0;
  }

  selectSection(section: HelpSection): void {
    this.persistActiveSectionContent();
    this.activeSectionId = section.id;
    this.loadActiveSectionIntoEditor();
  }

  addSection(): void {
    const raw = prompt(
      "Clave de traducción del menú (ej. modo-mapa).\nSe guardará como clave i18n y se mostrará traducida en el visor."
    );
    if (!raw?.trim()) return;

    const titleKey = normalizeSectionTitleKey(raw);
    if (!titleKey) return;

    this.persistActiveSectionContent();

    const id = uniqueSectionId(titleKey, this.sections);
    const maxOrder = this.sections.reduce((m, s) => Math.max(m, s.order), -1);
    const newSection: HelpSection = {
      id,
      title: titleKey,
      content: `<h2>${titleKey}</h2><p></p>`,
      order: maxOrder + 1,
    };

    this.sections = [...this.sections, newSection];
    this.activeSectionId = id;
    this.emitSections();
    this.loadActiveSectionIntoEditor();
  }

  renameSection(section: HelpSection, event: MouseEvent): void {
    event.stopPropagation();
    const raw = prompt(
      "Nueva clave de traducción (ej. modo-mapa)",
      section.title
    );
    if (!raw?.trim()) return;

    const titleKey = normalizeSectionTitleKey(raw);
    if (!titleKey || titleKey === section.title) return;

    this.persistActiveSectionContent(false);

    this.sections = this.sections.map((s) =>
      s.id === section.id ? { ...s, title: titleKey } : s
    );
    this.emitSections();
  }

  deleteSection(section: HelpSection, event: MouseEvent): void {
    event.stopPropagation();
    if (!confirm(`¿Eliminar la sección "${section.title}"?`)) return;

    this.persistActiveSectionContent();
    this.sections = this.sections.filter((s) => s.id !== section.id);

    if (this.activeSectionId === section.id) {
      this.activeSectionId = this.sortedSections[0]?.id ?? null;
    }

    this.emitSections();
    this.loadActiveSectionIntoEditor();
  }

  moveSectionUp(section: HelpSection, event: MouseEvent): void {
    event.stopPropagation();
    if (this.isFirst(section)) return;

    this.persistActiveSectionContent();
    this.sections = moveSectionByOffset(this.sections, section.id, -1);
    this.emitSections();
  }

  moveSectionDown(section: HelpSection, event: MouseEvent): void {
    event.stopPropagation();
    if (this.isLast(section)) return;

    this.persistActiveSectionContent();
    this.sections = moveSectionByOffset(this.sections, section.id, 1);
    this.emitSections();
  }

  isFirst(section: HelpSection): boolean {
    const sorted = this.sortedSections;
    return sorted.length > 0 && sorted[0].id === section.id;
  }

  isLast(section: HelpSection): boolean {
    const sorted = this.sortedSections;
    return sorted.length > 0 && sorted[sorted.length - 1].id === section.id;
  }

  onEditorReady(editor: any): void {
    this.editorInstance = editor;
    attachHelpEditorPasteSanitizer(editor);
    editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) =>
      new CkUploadAdapter(loader, this.http);
  }

  /**
   * Vuelca el HTML del editor en la sección activa y notifica al padre.
   * Debe llamarse antes de guardar si el usuario no cambió de sección.
   */
  flushPendingContent(): HelpSection[] {
    return this.persistActiveSectionContent(true);
  }

  onEditorContentChange(): void {
    if (this.syncingFromParent) return;
    this.persistActiveSectionContent(false);
  }

  private loadActiveSectionIntoEditor(): void {
    const section = this.sections.find((s) => s.id === this.activeSectionId);
    this.syncingFromParent = true;
    this.sectionContentControl.setValue(
      sanitizeEditorOutput(section?.content ?? ""),
      { emitEvent: false }
    );
    this.syncingFromParent = false;
  }

  private getActiveEditorHtml(): string {
    if (this.editorInstance?.getData) {
      return this.editorInstance.getData();
    }
    return this.sectionContentControl.value ?? "";
  }

  private persistActiveSectionContent(emit = true): HelpSection[] {
    if (!this.activeSectionId) {
      return [...this.sections];
    }

    const content = sanitizeEditorOutput(this.getActiveEditorHtml());
    this.sections = this.sections.map((s) =>
      s.id === this.activeSectionId ? { ...s, content } : s
    );
    if (emit) {
      this.emitSections();
    }
    return [...this.sections];
  }

  private emitSections(): void {
    this.sectionsChange.emit([...this.sections]);
  }
}
